/**
 * DOOR LEAVES — the one place that says how many doors a section has, how wide
 * each is, and which of them are hinged.
 *
 * The owner, 2026-08-09: *"a 'door section' must have the ability to have two
 * doors (i.e. a left/right door — which excluding the exceptions such as the
 * blind corner, will be hinged so require a handle on both doors) and be the
 * same size (v. the blind corner scenario where the false front side may not
 * be the same width as the door side)."*
 *
 * Three requirements sit in that sentence, and this module exists so all three
 * are answered by the SAME call:
 *
 *  1. a wide opening is a left/right PAIR — already what the elevation drew;
 *  2. the leaves of a pair are the SAME WIDTH — enforced here, not assumed;
 *  3. every hinged leaf takes a HANDLE — which the purchasing take-off was
 *     missing entirely, because it only ever walked drawer faces.
 *
 * Split out of `src/ui/elevation-interior.js` for that third reason: the
 * take-off (src/takeoff/handle-takeoff.js) must count exactly the leaves the
 * elevation draws, and it cannot import a UI module to do it. This is the same
 * discipline `src/geometry/handles.js` already applies to drawer pulls — one
 * function, two callers, so a drawing that shows two doors can never sit
 * beside a report that ordered one handle.
 *
 * **THE BLIND-CORNER SEAM.** A leaf carries `hinged`. Today every leaf this
 * module produces is hinged, so the count is simply the leaf count; the field
 * is what a blind corner will set to `false` on its false-front side, and the
 * handle count already reads it. That is the whole extension point — one
 * property, no machinery — chosen deliberately over building unequal-width
 * leaves now: the owner's priority is a working BASIC sketch, and the only
 * obligation the detailed work puts on today's code is that a section's doors
 * be a LIST rather than a single implied face.
 *
 * Pure — no DOM, no model imports — so both callers are node-testable.
 */

const EPS = 0.01;

/** A door leaf's pull sits this far in from the leaf's LATCH edge. */
const DOOR_PULL_INSET_UNITS = 1.5;
/** Defaults only; the app always passes the resolved `door_pair_*` rules. */
const DEFAULT_PAIR_CENTER_REVEAL_UNITS = 0.125;
const DEFAULT_PAIR_MIN_WIDTH_UNITS = 24;

/**
 * The `door_pair_*` rules, accepting EITHER naming: the resolved business
 * rules call them `doorPairMinWidthUnits` / `doorPairCenterRevealUnits`, while
 * the elevation has always passed them through its mark feed as
 * `pairMinWidthUnits` / `pairCenterRevealUnits`. Reading both means the
 * take-off can hand this module the rules object unmapped and the renderer
 * keeps its existing feed — one rule, two spellings, no third source.
 */
function pairRules(opts = {}) {
  const reveal = [opts.doorPairCenterRevealUnits, opts.pairCenterRevealUnits]
    .find((v) => Number.isFinite(v) && v >= 0);
  const minPair = [opts.doorPairMinWidthUnits, opts.pairMinWidthUnits]
    .find((v) => Number.isFinite(v) && v > 0);
  return {
    revealUnits: reveal ?? DEFAULT_PAIR_CENTER_REVEAL_UNITS,
    minPairWidthUnits: minPair ?? DEFAULT_PAIR_MIN_WIDTH_UNITS,
  };
}

/**
 * One door BAND's leaves across an opening.
 *
 * A pair splits the opening down the middle with a reveal between the leaves,
 * **both leaves exactly the same width** (owner, above). An opening narrower
 * than the pair minimum gets ONE leaf, because a 12" pair is two 6" doors
 * nobody orders.
 *
 * @param {number} widthUnits the OPENING width the band covers.
 * @param {object} [opts] the resolved business rules (either spelling).
 * @returns {Array<{side:'single'|'left'|'right', fromX:number, toX:number,
 *   widthUnits:number, hinged:boolean, pullX:number}>} left to right.
 */
export function doorLeaves(widthUnits, opts = {}) {
  const w = widthUnits > 0 ? widthUnits : 0;
  const { revealUnits, minPairWidthUnits } = pairRules(opts);
  const pullIn = (leafW) => Math.min(DOOR_PULL_INSET_UNITS, leafW * 0.16);
  if (!(w > 0)) return [];
  if (w + EPS < minPairWidthUnits || !(w - revealUnits > EPS)) {
    // One leaf, hinged left, pull at the right (the ADR 0055 drawing).
    return [{
      side: 'single', fromX: 0, toX: w, widthUnits: w, hinged: true, pullX: w - pullIn(w),
    }];
  }
  // EQUAL leaves: the reveal comes out of the middle, so each is exactly half
  // of what remains. Anything that later makes them unequal (a blind corner)
  // is an override on top of this, never a different formula.
  const leafW = (w - revealUnits) / 2;
  const rightStart = leafW + revealUnits;
  return [
    { side: 'left', fromX: 0, toX: leafW, widthUnits: leafW, hinged: true, pullX: leafW - pullIn(leafW) },
    { side: 'right', fromX: rightStart, toX: w, widthUnits: leafW, hinged: true, pullX: rightStart + pullIn(leafW) },
  ];
}

/**
 * How many door BANDS a coverage draws — `split` is an upper and a lower band
 * of doors over one opening, so it is TWO bands of leaves; every other
 * coverage is one.
 *
 * Deliberately derived from the coverage alone, with no solved interior: the
 * take-off reads compositions (see its header), and a band's EXTENT needs the
 * solve while the band COUNT never does.
 */
export function doorBandCount(doors) {
  if (!doors || typeof doors.coverage !== 'string') return 0;
  return doors.coverage === 'split' ? 2 : 1;
}

/**
 * Every HINGED door leaf a section's doors put on an opening — bands × leaves,
 * with false fronts (`hinged: false`) excluded.
 *
 * This is the handle quantity for doors: one handle per hinged leaf. The
 * two-pull rule is a DRAWER rule and is not applied here — it keys off a
 * 32"-wide face, and a door leaf that wide is a case the owner has not
 * described.
 *
 * @param {object|null} doors the section's normalized doors.
 * @param {number} openingUnits the opening the doors cover.
 * @param {object} [opts] the resolved business rules.
 * @returns {Array<{part:number, side:string, widthUnits:number}>} one entry
 *   per hinged leaf, so callers can total it or break it down.
 */
export function hingedDoorLeaves(doors, openingUnits, opts = {}) {
  const bands = doorBandCount(doors);
  if (!(bands > 0) || !(openingUnits > 0)) return [];
  const leaves = doorLeaves(openingUnits, opts).filter((leaf) => leaf.hinged !== false);
  const out = [];
  for (let part = 0; part < bands; part += 1) {
    for (const leaf of leaves) {
      out.push({ part, side: leaf.side, widthUnits: leaf.widthUnits });
    }
  }
  return out;
}

const SPACE_KINDS = new Set(['openSpace', 'rod', 'shelfGroup', 'shoeShelves', 'drawerBank']);

/** Finds one admin-maintained door style without fuzzy matching. */
export function doorStyleByKey(styles, key) {
  if (typeof key !== 'string' || !key) return null;
  return (Array.isArray(styles) ? styles : []).find((style) => style?.key === key) ?? null;
}

/**
 * Equal-width leaves using the STYLE maximum, not the old global pair
 * threshold. The smallest count that keeps every leaf within the maximum is
 * derived; no leaf count is stored on the sketch.
 */
export function doorLeavesForStyle(widthUnits, style, opts = {}) {
  const width = Number(widthUnits);
  const max = Number(style?.maxLeafWidthUnits);
  if (!(width > 0) || !(max > 0)) return [];
  const reveal = Number.isFinite(opts.doorPairCenterRevealUnits) && opts.doorPairCenterRevealUnits >= 0
    ? opts.doorPairCenterRevealUnits : DEFAULT_PAIR_CENTER_REVEAL_UNITS;
  const count = Math.max(1, Math.ceil((width + reveal) / (max + reveal)));
  const leafWidth = (width - reveal * (count - 1)) / count;
  if (!(leafWidth > 0)) return [];
  const pullIn = Math.min(DOOR_PULL_INSET_UNITS, leafWidth * 0.16);
  return Array.from({ length: count }, (_, index) => {
    const fromX = index * (leafWidth + reveal);
    const toX = fromX + leafWidth;
    // Adjacent leaves alternate their latch edge, preserving the established
    // two-leaf centre pulls while extending deterministically to wider runs.
    const latchRight = index % 2 === 0;
    return {
      side: count === 1 ? 'single' : `leaf-${index + 1}`,
      fromX,
      toX,
      widthUnits: leafWidth,
      hinged: true,
      pullX: latchRight ? toX - pullIn : fromX + pullIn,
    };
  });
}

/**
 * Resolves the designer's two endpoint element ids into the complete door
 * geometry. Vertical cuts are allowed ONLY at boundaries between selected
 * spaces. A boundary through a shelf is its centre (the midpoint between the
 * adjacent clear-space edges), so the two faces meet without a gap/overlap.
 * The greedy farthest-legal boundary gives the fewest height bands.
 *
 * @returns {{ok:true,fromUnits:number,toUnits:number,bands:Array<object>,
 *   leaves:Array<object>,areaSqFt:number}|{ok:false,reason:string}}
 */
export function deriveDoorGeometry(doors, solvedElements, widthUnits, style, opts = {}) {
  if (!doors?.fromElementId || !doors?.toElementId) {
    return { ok: false, reason: 'door has no resolvable covered-space extent' };
  }
  if (!style) {
    return { ok: false, reason: `door style ${doors?.styleKey ?? '(none)'} is not in the door-style rule` };
  }
  // Resolve the identity range BEFORE dropping collapsed spaces. C3 keeps its
  // optional top-opening element (and therefore its persistent id) when an
  // 84-inch DH omits that opening at solve time. A door already naming that
  // endpoint must clamp to the surviving spaces inside the selected range,
  // not become unresolvable merely because the endpoint is now zero-height.
  const orderedSpaces = (Array.isArray(solvedElements) ? solvedElements : [])
    .filter((el) => SPACE_KINDS.has(el?.kind) && typeof el.id === 'string' && el.id
      && Number.isFinite(el.fromUnits) && Number.isFinite(el.toUnits));
  const a = orderedSpaces.findIndex((space) => space.id === doors.fromElementId);
  const b = orderedSpaces.findIndex((space) => space.id === doors.toElementId);
  if (a < 0 || b < 0) return { ok: false, reason: 'door extent references a space that no longer exists' };
  const selected = orderedSpaces.slice(Math.min(a, b), Math.max(a, b) + 1)
    .filter((space) => space.toUnits > space.fromUnits)
    .slice().sort((left, right) => left.fromUnits - right.fromUnits);
  if (!selected.length) return { ok: false, reason: 'door extent contains no surviving space' };
  const maxHeight = Number(style.maxLeafHeightUnits);
  if (!(maxHeight > 0)) return { ok: false, reason: `door style ${style.key} has no maximum leaf height` };
  const leaves = doorLeavesForStyle(widthUnits, style, opts);
  if (!leaves.length) return { ok: false, reason: `door style ${style.key} has no maximum leaf width` };

  const fromUnits = selected[0].fromUnits;
  const toUnits = selected[selected.length - 1].toUnits;
  const boundaries = selected.slice(0, -1).map((space, index) => (
    (space.toUnits + selected[index + 1].fromUnits) / 2
  ));
  const bands = [];
  let from = fromUnits;
  while (toUnits - from > maxHeight + EPS) {
    const cut = boundaries.filter((at) => at > from + EPS && at - from <= maxHeight + EPS).at(-1);
    if (!Number.isFinite(cut)) {
      return { ok: false, reason: `a selected space exceeds ${maxHeight}in; split the space before adding this door style` };
    }
    bands.push({ part: `band-${bands.length + 1}`, fromUnits: from, toUnits: cut, clamped: false });
    from = cut;
  }
  bands.push({ part: `band-${bands.length + 1}`, fromUnits: from, toUnits, clamped: false });
  const areaSqFt = widthUnits * (toUnits - fromUnits) / 144;
  return { ok: true, fromUnits, toUnits, bands, leaves, areaSqFt };
}
